import type { StoreApi, UseBoundStore } from 'zustand';
import type { WithSelectors } from '../../../utils/state/zustand';
import { createInstanceManager, type InstanceManager } from '../createInstanceManager';
import type {
  DataSourceRecordStoreInstanceState,
  DataSourceRecordStoreState,
  DataSourceRecordStoreType,
} from './types';

export type DataSourceRecordStoreInstance = Omit<
  WithSelectors<UseBoundStore<StoreApi<DataSourceRecordStoreType>>>,
  'subscribe'
> & {
  subscribe: {
    (
      listener: (state: DataSourceRecordStoreType, prevState: DataSourceRecordStoreType) => void,
    ): () => void;
    <T>(
      selector: (state: DataSourceRecordStoreType) => T,
      listener: (selectedState: T, previousSelectedState: T) => void,
      options?: { equalityFn?: (a: T, b: T) => boolean; fireImmediately?: boolean },
    ): () => void;
  };
};

export type CreateInstanceDataSourceRecordStoreFactory = (
  initial: Partial<DataSourceRecordStoreState>,
) => DataSourceRecordStoreInstance;

export type DataSourceRecordStoreManager = InstanceManager<
  DataSourceRecordStoreInstance,
  DataSourceRecordStoreInstanceState['dataSources']
>;

/**
 * Thin adapter over createInstanceManager. Wraps the incoming dataSources map into the
 * store's expected initial shape before delegating.
 */
export function createDataSourceRecordStoreManager(
  baseStore: DataSourceRecordStoreInstance,
  createStore: CreateInstanceDataSourceRecordStoreFactory,
): DataSourceRecordStoreManager {
  return createInstanceManager<
    DataSourceRecordStoreInstance,
    DataSourceRecordStoreInstanceState['dataSources']
  >(
    baseStore,
    (_instanceId, dataSources) => createStore({ dataSources: dataSources ?? {} }),
    () => 'Missing DataSourceRecordStoreProvider',
  );
}
